/**
 *
 * UserConsent
 *
 */

import React, { memo, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import Fade from '@material-ui/core/Fade';
import { showPrivacyPolicy } from 'containers/Main/actions';
import { makeSelectAuth, makeSelectProfile } from 'containers/Auth/selectors';
import isBefore from 'date-fns/is_before';
import useCookie from './hooks/useCookie';

const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: '#01619b',
    color: '#fff',
    padding: '1em 1.8em',
    width: '100%',
    position: 'fixed',
    left: 0,
    bottom: 0,
    right: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
    justifyContent: 'space-between',
    zIndex: 9999,
    flexWrap: 'nowrap',
    opacity: ({ opacity }) => opacity,
    transition: 'all 2s ease-in-out',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  text: {
    lineHeight: '1.5em',
    marginRight: '1em',
    [theme.breakpoints.down('xs')]: {
      marginBottom: '1em',
      marginRight: 0,
    },
  },
  textLink: {
    cursor: 'pointer',
  },
  button: {
    borderRadius: 5,
    backgroundColor: '#ffa900',
    color: '#fff',
    padding: 8,
    fontSize: '0.875rem',
    lineHeight: '1.5em',
    fontWeight: '700',
    minWidth: 140,
  },
}));

const UserConsent = memo(() => {
  const classes = useStyles();
  const auth = useSelector(makeSelectAuth());
  const profile = useSelector(makeSelectProfile());
  const firebase = useFirebase();
  const [status, setStatus] = useCookie('cookieconsent_status');
  const [privacy, setPrivacy] = useCookie('cookieconsent_privacy');
  const [definitiveText, setDefinitiveText] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => setDefinitiveText(null), [auth, profile]);
  const createdAt = !auth.isEmpty
    ? new Date(Number(auth.createdAt))
    : Date.now();
  const privacyUpdated = new Date(2020, 2, 6);
  const shouldShowByDate = isBefore(createdAt, privacyUpdated);
  const visible =
    !definitiveText &&
    (status !== 'dismiss' ||
      (!auth.isEmpty &&
        shouldShowByDate &&
        !profile.privacyConsent &&
        privacy !== 'dismiss'));

  const openPrivacyPolicy = () => dispatch(showPrivacyPolicy(true));

  const COOKIE_CONSENT = useMemo(
    () => (
      <Typography variant="body2" className={classes.text}>
        This website uses cookies to ensure you get the best experience on our
        website.
      </Typography>
    ),
    [],
  );
  const PRIVACY_CONSENT = useMemo(
    () => (
      <Typography variant="body2" className={classes.text}>
        Our{' '}
        <b
          className={classes.textLink}
          role="button"
          tabIndex={0}
          onClick={openPrivacyPolicy}
        >
          Privacy Policy
        </b>{' '}
        has been updated. We do not share your data with anyone and we do not
        collect credit card info.
      </Typography>
    ),
    [],
  );

  if (!auth.isLoaded || !profile.isLoaded) return null;

  const text =
    definitiveText || (status !== 'dismiss' ? COOKIE_CONSENT : PRIVACY_CONSENT);

  const clickHandler =
    status !== 'dismiss'
      ? () => {
          setDefinitiveText(COOKIE_CONSENT);
          setStatus('dismiss', {
            expires: 365,
          });
        }
      : () => {
          setDefinitiveText(PRIVACY_CONSENT);
          setPrivacy('dismiss', {
            expires: 365,
          });
          firebase.updateProfile({ privacyConsent: true });
        };

  return (
    <Fade in={visible} timeout={1000}>
      <div className={classes.container}>
        {text}
        <ButtonBase className={classes.button} onClick={clickHandler}>
          Got it!
        </ButtonBase>
      </div>
    </Fade>
  );
});

export default UserConsent;
